import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
  Alert as MuiAlert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as ResolveIcon,
  DoNotDisturb as IgnoreIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  NotificationsActive as AlertIcon,
  Search as SearchIcon,
  Check as CheckCircle,
} from '@mui/icons-material';
import { ALERT_SEVERITY, ALERT_STATUS } from '../utils/constants';
import api from '../utils/api';

// Alias for CheckCircle to match the component name
const CheckCircleIcon = CheckCircle;

// Comment out unused variables to fix lint warnings
// const STATUS_COLORS = {};

// Alert severity colors
const severityColors = {
  [ALERT_SEVERITY.INFO]: 'info',
  [ALERT_SEVERITY.WARNING]: 'warning',
  [ALERT_SEVERITY.ERROR]: 'error',
  [ALERT_SEVERITY.CRITICAL]: 'error',
};

// Alert status colors
const statusColors = {
  [ALERT_STATUS.ACTIVE]: 'error',
  [ALERT_STATUS.ACKNOWLEDGED]: 'warning',
  [ALERT_STATUS.RESOLVED]: 'success',
  [ALERT_STATUS.IGNORED]: 'default',
};

const AlertsPage = () => {
  // States
  const [alerts, setAlerts] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [isAlertDetailsOpen, setIsAlertDetailsOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filterPipelineId, setFilterPipelineId] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch alerts from the API
  const fetchAlerts = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      
      // Add filters to the request if they are set
      if (filterPipelineId) params.append('pipeline_id', filterPipelineId);
      if (filterSeverity) params.append('severity', filterSeverity);
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await api.get(`/alerts/?${params.toString()}`);
      
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch alerts. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch pipelines for the filter dropdown
  const fetchPipelines = async () => {
    try {
      const response = await api.get('/pipelines/');
      setPipelines(response.data);
    } catch (error) {
      console.error('Error fetching pipelines:', error);
    }
  };

  // Load data when component mounts and when filters change
  useEffect(() => {
    fetchAlerts();
    fetchPipelines();
  }, [filterPipelineId, filterSeverity, filterStatus]);

  // Handle alert resolution
  const handleResolveAlert = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/resolve`, {});
      fetchAlerts(); // Refresh alerts
      setSnackbar({
        open: true,
        message: 'Alert resolved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error resolving alert:', error);
      setSnackbar({
        open: true,
        message: 'Failed to resolve alert',
        severity: 'error'
      });
    }
  };

  // Handle alert acknowledgment
  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await api.post(`/alerts/${alertId}/acknowledge`, {});
      fetchAlerts(); // Refresh alerts
      setSnackbar({
        open: true,
        message: 'Alert acknowledged',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      setSnackbar({
        open: true,
        message: 'Failed to acknowledge alert',
        severity: 'error'
      });
    }
  };

  // Handle filter reset
  const handleResetFilters = () => {
    setFilterPipelineId('');
    setFilterSeverity('');
    setFilterStatus('');
    setSearchQuery('');
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Filter alerts by search query
  const filteredAlerts = alerts.filter(alert => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return (
      alert.title.toLowerCase().includes(searchLower) ||
      alert.message.toLowerCase().includes(searchLower) ||
      (alert.pipeline_name && alert.pipeline_name.toLowerCase().includes(searchLower))
    );
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Alerts
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={fetchAlerts}
          disabled={isLoading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Pipeline</InputLabel>
            <Select
              value={filterPipelineId}
              onChange={(e) => setFilterPipelineId(e.target.value)}
              label="Pipeline"
            >
              <MenuItem value="">All Pipelines</MenuItem>
              {pipelines.map((pipeline) => (
                <MenuItem key={pipeline.id} value={pipeline.id}>
                  {pipeline.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Severity</InputLabel>
            <Select
              value={filterSeverity}
              onChange={(e) => setFilterSeverity(e.target.value)}
              label="Severity"
            >
              <MenuItem value="">All Severities</MenuItem>
              {Object.entries(ALERT_SEVERITY).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {value.charAt(0).toUpperCase() + value.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="">All Statuses</MenuItem>
              {Object.entries(ALERT_STATUS).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {value.charAt(0).toUpperCase() + value.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={handleResetFilters}
            disabled={!filterPipelineId && !filterSeverity && !filterStatus && !searchQuery}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Alerts Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Pipeline</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Triggered At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredAlerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="textSecondary">
                      No alerts found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAlerts.map((alert) => (
                  <TableRow key={alert.id} hover>
                    <TableCell>{alert.title}</TableCell>
                    <TableCell>{alert.pipeline_name || 'N/A'}</TableCell>
                    <TableCell>
                      <Tooltip title={alert.message} arrow>
                        <Typography noWrap sx={{ maxWidth: 300 }}>
                          {alert.message.length > 50 
                            ? `${alert.message.substring(0, 50)}...` 
                            : alert.message}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={alert.severity} 
                        color={severityColors[alert.severity] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={alert.status} 
                        color={statusColors[alert.status] || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(alert.triggered_at)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => {
                              setSelectedAlert(alert);
                              setIsAlertDetailsOpen(true);
                            }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {alert.status === ALERT_STATUS.ACTIVE && (
                          <>
                            <Tooltip title="Acknowledge">
                              <IconButton 
                                size="small" 
                                color="warning"
                                onClick={() => handleAcknowledgeAlert(alert.id)}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Resolve">
                              <IconButton 
                                size="small" 
                                color="success"
                                onClick={() => handleResolveAlert(alert.id)}
                              >
                                <ResolveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {alert.status === ALERT_STATUS.ACKNOWLEDGED && (
                          <Tooltip title="Resolve">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleResolveAlert(alert.id)}
                            >
                              <ResolveIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Alert Details Dialog */}
      <Dialog
        open={isAlertDetailsOpen}
        onClose={() => setIsAlertDetailsOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedAlert && (
          <>
            <DialogTitle>{selectedAlert.title}</DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">Pipeline</Typography>
                <Typography>{selectedAlert.pipeline_name || 'N/A'}</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">Severity</Typography>
                <Chip 
                  label={selectedAlert.severity} 
                  color={severityColors[selectedAlert.severity] || 'default'}
                  size="small"
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                <Chip 
                  label={selectedAlert.status} 
                  color={statusColors[selectedAlert.status] || 'default'}
                  size="small"
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">Triggered At</Typography>
                <Typography>{formatDate(selectedAlert.triggered_at)}</Typography>
              </Box>
              {selectedAlert.acknowledged_at && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Acknowledged At</Typography>
                  <Typography>{formatDate(selectedAlert.acknowledged_at)}</Typography>
                </Box>
              )}
              {selectedAlert.resolved_at && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Resolved At</Typography>
                  <Typography>{formatDate(selectedAlert.resolved_at)}</Typography>
                </Box>
              )}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="textSecondary">Message</Typography>
                <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selectedAlert.message}</Typography>
              </Box>
              {selectedAlert.context && Object.keys(selectedAlert.context).length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="textSecondary">Context</Typography>
                  <Box component="pre" sx={{ 
                    p: 2, 
                    bgcolor: 'background.default', 
                    borderRadius: 1,
                    maxHeight: '300px',
                    overflow: 'auto'
                  }}>
                    {JSON.stringify(selectedAlert.context, null, 2)}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setIsAlertDetailsOpen(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <MuiAlert 
          elevation={6} 
          variant="filled" 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default AlertsPage;
